import React from "react";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import "./LandingPage.css";

/**
 * kurnal.ai marketing landing page — paper-terminal style (mockup-5).
 *
 * This is the default, unauthenticated view served by the indexing-client.
 * Every "get access" style call-to-action opens Clerk's sign-up flow;
 * "Sign in" opens Clerk's sign-in flow. Once a user is authenticated,
 * App.tsx swaps this out for the real dashboard (Sidebar + pages).
 */

const INFRA = [
  "PDF & HTML Parsing",
  "Vector + Keyword Search",
  "Event-driven Pipeline (Kafka)",
  "Object Storage (S3)",
  "LLM Summarization",
  "Typesense Indexing",
  "PostgreSQL Metadata",
];

const TapeItems: React.FC<{ hidden?: boolean }> = ({ hidden }) => (
  <div style={{ display: "inline-flex" }} aria-hidden={hidden || undefined}>
    {INFRA.map((label) => (
      <span key={label} className="tk">
        <b>▪</b> {label}
      </span>
    ))}
  </div>
);

const LandingPage: React.FC = () => {
  return (
    <div className="kurnal-landing">
      {/* ============ NAV ============ */}
      <header className="site-header">
        <div className="nav">
          <a href="#top" className="brand">
            <span className="cursor-block" /> kurnal<span className="tld">.ai</span>
          </a>
          <nav className="nav-links">
            <a href="#product">Product</a>
            <a href="#how">How it works</a>
            <a href="#api">API</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="nav-cta">
            <SignInButton mode="modal">
              <button className="signin">Sign in</button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button className="btn-ink">Get early access</button>
            </SignUpButton>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section id="top" className="hero">
        <span className="hero-tag">
          <span className="dot" /> now indexing filings, transcripts &amp; PDFs in real time
        </span>
        <h1>
          The document store
          <br />
          <b>built for AI agents.</b>
        </h1>
        <p className="sub">
          kurnal.ai ingests any file, filing, or URL — parses it, chunks it, and indexes it into
          structured, retrievable memory. One API call and your agent has grounded answers, every time.
        </p>
        <div className="cta-row">
          <SignUpButton mode="modal">
            <button className="btn-primary">Get early access →</button>
          </SignUpButton>
          <a href="#api" className="btn-ghost">View the API</a>
        </div>
        <span className="free-note">No credit card · Free while in beta</span>
      </section>

      {/* ============ HERO CONSOLE: live query ============ */}
      <div className="console-wrap">
        <div className="console">
          <div className="console-bar">
            <span className="d d1" />
            <span className="d d2" />
            <span className="d d3" />
            <span className="title">kurnal — agent-query.ts</span>
          </div>
          <div className="tape">
            <div className="tape-track">
              <TapeItems />
              <TapeItems hidden />
            </div>
          </div>
          <div className="console-body">
            <div className="console-code">
              <div className="c-dim">{"// ask kurnal for grounded context"}</div>
              <div>
                <span className="c-kw">const</span> res = <span className="c-kw">await</span> kurnal.query({"{"}
              </div>
              <div style={{ paddingLeft: 18 }}>
                q: <span className="c-str">"Q3 revenue guidance?"</span>,
              </div>
              <div style={{ paddingLeft: 18 }}>
                tenantId: <span className="c-str">"acme-agent"</span>,
              </div>
              <div style={{ paddingLeft: 18 }}>
                topK: <span className="c-num">5</span>
              </div>
              <div>{"}"});</div>
              <div className="c-dim" style={{ marginTop: 12 }}>
                {"// → grounded chunks, cited & ranked"}
                <span className="caret" />
              </div>
            </div>
            <div className="console-results">
              <div className="result-row">
                <span className="glyph">[1]</span>
                <p style={{ margin: 0 }}>
                  <b>Q3-earnings-transcript.pdf</b>
                  <span className="c-dim"> — "...revenue guidance raised to $4.1B, up 12% YoY..."</span>
                </p>
              </div>
              <div className="result-row">
                <span className="glyph">[2]</span>
                <p style={{ margin: 0 }}>
                  <b>10-Q_filing.htm</b>
                  <span className="c-dim"> — "...forward guidance reflects continued momentum in..."</span>
                </p>
              </div>
              <div className="result-row">
                <span className="glyph">[3]</span>
                <p style={{ margin: 0 }}>
                  <b>investor-update.pdf</b>
                  <span className="c-dim"> — "...guided range for the quarter narrowed to..."</span>
                </p>
              </div>
              <div className="result-meta">
                <span className="c-grn">✓</span> 128ms · 3 sources · relevance 0.94
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ PRODUCT / FEATURES (man page) ============ */}
      <section id="product" className="block">
        <div className="sec-head">kurnal(1) — capabilities</div>
        <h2>
          Every document, <b>one memory layer.</b>
        </h2>
        <p className="sec-sub">
          Filings, transcripts, PDFs, web pages — kurnal.ai turns messy, unstructured documents
          into clean, queryable knowledge your agents can trust.
        </p>

        <div className="man-grid">
          <div className="man-item">
            <span className="flag">ingest</span>
            <p>Upload files directly or hand over a URL. PDFs, HTML filings, transcripts, and reports are pulled in automatically.</p>
          </div>
          <div className="man-item">
            <span className="flag">parse</span>
            <p>Documents are cleaned, chunked, and enriched with metadata and LLM-generated summaries — automatically, at scale.</p>
          </div>
          <div className="man-item">
            <span className="flag">retrieve</span>
            <p>Full-text and semantic search out of the box. Your agent asks a question, kurnal returns cited, ranked context.</p>
          </div>
          <div className="man-item">
            <span className="flag">stream</span>
            <p>An event-driven pipeline moves documents from upload to searchable index in seconds, not hours.</p>
          </div>
          <div className="man-item">
            <span className="flag">tenant</span>
            <p>Every document, index, and query is scoped to an owner or tenant — safe for agents serving many customers.</p>
          </div>
          <div className="man-item">
            <span className="flag">status</span>
            <p>Track document status end-to-end — uploaded, parsed, indexed, or failed — with a single query.</p>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="block">
        <div className="sec-head">how it works</div>
        <h2>
          From raw file to <b>grounded answer.</b>
        </h2>

        <div className="steps">
          <div className="step">
            <span className="n">[01]</span>
            <h3>Upload or link a document</h3>
            <p>Send a file or a URL to kurnal's ingestion endpoint. Filings, PDFs, transcripts, and web pages all work the same way.</p>
          </div>
          <div className="step">
            <span className="n">[02]</span>
            <h3>kurnal parses &amp; indexes it</h3>
            <p>Workers extract text, chunk content, generate summaries, and push it into a structured, searchable index — automatically.</p>
          </div>
          <div className="step">
            <span className="n">[03]</span>
            <h3>Your agent queries kurnal</h3>
            <p>One API call returns ranked, cited context — so every answer your agent gives is grounded in real source material.</p>
          </div>
        </div>
      </section>

      {/* ============ API SECTION ============ */}
      <section id="api" className="block">
        <div className="sec-head">simple by design</div>
        <div className="api-grid">
          <div>
            <h2>
              Two calls. <b>That's the whole integration.</b>
            </h2>
            <p className="sec-sub">
              Push a document in, pull grounded context out. No pipelines to babysit, no vector
              store to manage — kurnal.ai handles ingestion, parsing, chunking, and indexing behind
              one clean API.
            </p>
            <ul className="check-list">
              <li><span className="tick">✓</span> Owner &amp; tenant scoped from day one</li>
              <li><span className="tick">✓</span> Full-text + semantic search, unified</li>
              <li><span className="tick">✓</span> Status tracking for every document</li>
            </ul>
          </div>

          <div className="console">
            <div className="console-bar">
              <span className="d d1" />
              <span className="d d2" />
              <span className="d d3" />
              <span className="title">terminal</span>
            </div>
            <pre>
              <code>
                <span className="c-dim"># 1. store a document</span>{"\n"}
                <span className="c-grn">$</span> curl -X POST https://api.kurnal.ai/v1/documents \{"\n"}
                {"    "}-H <span className="c-str">"Authorization: Bearer $KURNAL_KEY"</span> \{"\n"}
                {"    "}-F <span className="c-str">"file=@earnings-call-q3.pdf"</span> \{"\n"}
                {"    "}-F <span className="c-str">"owner=acme-agent"</span>{"\n\n"}
                <span className="c-dim"># 2. ask your agent's memory a question</span>{"\n"}
                <span className="c-grn">$</span> curl -X POST https://api.kurnal.ai/v1/query \{"\n"}
                {"    "}-H <span className="c-str">"Authorization: Bearer $KURNAL_KEY"</span> \{"\n"}
                {"    "}-d <span className="c-str">{'\'{"q":"What was Q3 revenue guidance?","topK":5}\''}</span>
                <span className="caret" />
              </code>
            </pre>
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="block" style={{ paddingBottom: 60 }}>
        <div className="stats">
          <div className="stat">
            <div className="v">&lt;150ms</div>
            <p>Median query latency</p>
          </div>
          <div className="stat">
            <div className="v">10+</div>
            <p>Document types supported</p>
          </div>
          <div className="stat">
            <div className="v">100%</div>
            <p>Cited, source-grounded</p>
          </div>
          <div className="stat">
            <div className="v">24/7</div>
            <p>Streaming ingestion</p>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section id="faq" className="block">
        <div className="sec-head">faq — good to know</div>
        <div className="faq">
          <details>
            <summary>
              What kinds of documents can kurnal.ai index?
              <span className="plus">+</span>
            </summary>
            <p>PDFs, HTML pages, regulatory filings, earnings transcripts, and general web URLs. If it has text, kurnal can parse, chunk, and index it.</p>
          </details>
          <details>
            <summary>
              How does this plug into my agent stack?
              <span className="plus">+</span>
            </summary>
            <p>Call the upload endpoint whenever new documents arrive, and the query endpoint whenever your agent needs context. No vector store or search infra to run yourself.</p>
          </details>
          <details>
            <summary>
              Is my data isolated per tenant?
              <span className="plus">+</span>
            </summary>
            <p>Yes — every document, index entry, and query is scoped by owner/tenant, so multi-agent and multi-customer deployments stay isolated by default.</p>
          </details>
          <details>
            <summary>
              How fast is a document searchable after upload?
              <span className="plus">+</span>
            </summary>
            <p>Most documents are parsed and searchable within seconds thanks to an event-driven, streaming pipeline.</p>
          </details>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section id="cta" className="band">
        <h2>
          Give your agent a memory
          <br />
          it can <b>actually trust.</b>
        </h2>
        <p>
          Join the kurnal.ai beta and turn your document backlog into a live, queryable knowledge
          layer — in an afternoon.
        </p>
        <div className="cta-row">
          <SignUpButton mode="modal">
            <button className="btn-primary">Get early access →</button>
          </SignUpButton>
          <a href="mailto:hello@kurnal.ai" className="btn-ghost">Talk to us</a>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer>
        <a href="#top" className="brand" style={{ fontSize: 14 }}>
          <span className="cursor-block cursor-block--sm" /> kurnal<span className="tld">.ai</span>
        </a>
        <p style={{ margin: 0 }}>© 2026 kurnal.ai — the document store for AI agents.</p>
        <div className="links">
          <a href="mailto:hello@kurnal.ai">Privacy</a>
          <a href="mailto:hello@kurnal.ai">Terms</a>
          <a href="#top">Status</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
